import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import customFetch from "./apicall/api";
import { LoadingOutlined, DownloadOutlined } from "@ant-design/icons";
import DynamicForm from "./Report_dynamic_form";

const Form = ({ apiToken, reportListData }) => {
  // ========== State ==========
  const [reportConfig, setReportConfig] = useState(null);
  const [extraReportConfigFields, setExtraReportConfigFields] = useState(null);
  const [currentReportName, setCurrentReportName] = useState(null);
  const [currentReportId, setCurrentReportId] = useState(null);
  const [formState, setFormState] = useState({});
  const [isFetchingConfig, setIsFetchingConfig] = useState(false);
  const [isErrorWindowActive, setErrorWindowActive] = useState(false);
  const [errorWindowMessage, setErrorWindowMessage] = useState("");
  const [processedAtCount, setProcessedAtCount] = useState(0);
  const [isFetchingReportLog, setFetchingReportLog] = useState(false);
  const [reportData, setReportData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // ========== Polling ==========
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);

  const startPolling = () => {
    if (pollIntervalRef.current) return;
    setIsPolling(true);
    pollIntervalRef.current = setInterval(() => {
      getReportData(currentPage, pageSize);
    }, 5000);
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  // ========== Cleanup on unmount ==========
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // ========== Load data when report changes ==========
  useEffect(() => {
    if (currentReportId) {
      setProcessedAtCount(0);
      setReportData([]);
      setCurrentPage(1);
      getReportData(1, pageSize);
    }
  }, [currentReportId]);

  // ========== Submit handler ==========
  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();

    const uid = localStorage.getItem("in_userid");
    formData.append("userid", uid);
    formData.append("report_id", currentReportId);

    const fieldsToSubmit = [];
    let validFields = true;
    for (const key in formState) {
      if (formState.hasOwnProperty(key)) {
        const fieldState = formState[key];

        let is_req = true;
        if (reportConfig[key] && reportConfig[key].is_req !== null && reportConfig[key].is_req !== undefined) {
          is_req = reportConfig[key].is_req;
          is_req = (is_req === "1" || is_req === true || is_req === "true" || is_req === 1);
        }

        if (fieldState.isVisible && !fieldState.isFetching && fieldState.value && is_req) {
          if (Array.isArray(fieldState.value)) {
            fieldState.value.forEach((item) => fieldsToSubmit.push({ id: item, value: item }));
          } else {
            fieldsToSubmit.push({ id: fieldState.id, value: fieldState.value });
          }
        } else if (fieldState.isVisible && (fieldState.isFetching || !fieldState.value)) {
          validFields = false;
          break;
        }
      }
    }
    formData.append("user_parameter", JSON.stringify(fieldsToSubmit));

    if (!validFields || (fieldsToSubmit.length === 0 && reportConfig && Object.keys(reportConfig).length > 0)) {
      setErrorWindowMessage(`Error sending request: Please try again.`);
      setErrorWindowActive(true);
      return;
    }

    if (validFields && (fieldsToSubmit.length === 0 || !reportConfig || Object.keys(reportConfig).length === 0)) {
      let nadata = [{}];
      nadata["nakey"] = "NA";
      formData.append("user_parameter", JSON.stringify(nadata, null));
    }

    setIsFetchingConfig(true);

    customFetch(`${process.env.REACT_APP_URL}/store/desktop/report/request/detail`, {
      method: 'POST',
      headers: { 'api-token': apiToken },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.api_code === 4 || data.api_code === 3 || data.api_code === 5) {
          localStorage.clear();
          // If using React Router v6, replace with navigate("/");
          this.props.history.push("/");
        }
        setCurrentPage(1);
        getReportData(1, pageSize);
        startPolling();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setIsFetchingConfig(false));
  };

  // ========== Helper functions ==========
  const areVisibleFieldsComplete = () => {
    return formState && Object.keys(formState).every((key) => {
      const field = formState[key];
      const config = reportConfig[key];
      if (!field || !field.isVisible) return true;
      if (config.type === "textbox") return field.value && config.status === 1;
      return field.value !== "" && field.value !== null;
    });
  };

  const isAnyFieldFetching = () => {
    return Object.keys(formState).some((key) => formState[key] && formState[key].isFetching);
  };

  // We keep this for button logic but no longer use it for a preloader.
  const checkFetchingState = () => {
    return (
      isFetchingConfig ||
      isFetchingReportLog ||
      isAnyFieldFetching() ||
      (currentReportId && processedAtCount > 0)
    );
  };

  const downloadReportUpdateDate = async (request_no, download_link = "") => {
    const reqId = request_no.split("/").at(-1);
    try {
      const response = await customFetch(
        process.env.REACT_APP_URL + "/fetch/report/datetime?request_id=" + reqId,
        {
          method: "GET",
          headers: { "api-token": apiToken },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      window.location.href = download_link;
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getHours = (create_at) => {
    const dateOneObj = new Date(create_at);
    const dateTwoObj = new Date();
    const milliseconds = Math.abs(dateTwoObj - dateOneObj);
    return milliseconds / 36e5;
  };

  const getInputData = (inputdata) => {
    let x = inputdata;
    let result = "";

    if (Array.isArray(x)) {
      x.forEach((item) => {
        if (item.hasOwnProperty("value")) {
          let date = moment(item.value);
          if (date.isValid()) {
            let dateAdded = false;
            reportConfig && Object.keys(reportConfig).forEach((inputkey) => {
              const config = reportConfig[inputkey];
              if (
                config.type === "calendar_daterange" ||
                config.type === "calendar_date"
              ) {
                if (!dateAdded) {
                  result += moment(item.value).format("YYYY-MM-DD") + " | ";
                  dateAdded = true;
                }
              } else if (
                config.type === "calendar_yearmonth" ||
                config.type === "calendar_yearmonthrange"
              ) {
                if (!dateAdded) {
                  result += moment(item.value).format("MMM-YYYY") + " | ";
                  dateAdded = true;
                }
              }
            });
            if (!dateAdded) {
              result += item.value + " | ";
            }
          } else {
            result += item.value + " | ";
          }
        }
      });

      return result
        .trim()
        .replace(/\|([^|]*)$/, "$1")
        .trim();
    } else {
      const arr = Object.keys(x).map((key) => ({ [key]: x[key] }));
      return arr.map((arrdata) => {
        if (arrdata.month) {
          return arrdata.month;
        } else if (arrdata.lender_name) {
          return arrdata.lender_name;
        }
      });
    }
  };

  // ========== getReportData (polling target) ==========
  const getReportData = (page_number = 1, page_size = 50) => {
    setFetchingReportLog(true);
    const uid = localStorage.getItem("in_userid");

    const formdata = new FormData();
    formdata.append("userid", uid);
    formdata.append("report_id", currentReportId);
    formdata.append("page_number", page_number);
    formdata.append("page_size", page_size);

    customFetch(process.env.REACT_APP_URL + "/get/request/queue", {
      method: "POST",
      headers: {
      "api-token": apiToken,
      "Cache-Control": "no-cache, no-store",
      "Pragma": "no-cache",
    },
    cache: "no-store",
    body: formdata,
  }
  )
      .then((res) => res.json())
      .then((response) => {
        const pagination = response.pagination || {};
        const reportList = response.data || response || [];

        const {
          current_page = page_number,
          page_size: returnedPageSize = page_size,
          total_records = reportList.length,
          total_pages = Math.ceil(total_records / page_size),
        } = pagination;

        const processed_AtCount = reportList.reduce((count, item) => {
          return item.completed_at === null ? count + 1 : count;
        }, 0);

        setProcessedAtCount(processed_AtCount);
        setReportData(reportList);
        setTotalRecords(total_records);
        setTotalPages(total_pages);
        setCurrentPage(current_page);
        setPageSize(returnedPageSize);

        if (processed_AtCount === 0 && pollIntervalRef.current) {
          stopPolling();
        }
      })
      .catch((error) => {
        setErrorWindowActive(true);
        setErrorWindowMessage("Error fetching report history. Please try again.");
        setProcessedAtCount(99999);
        console.error("Error fetching report history: " + error);
        stopPolling();
      })
      .finally(() => {
        setFetchingReportLog(false);
      });
  };

  // ========== Pagination handlers ==========
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      getReportData(page, pageSize);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    getReportData(1, newSize);
  };

  const renderPagination = () => {
    if (reportData.length === 0) return null;

    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (endPage - startPage < 4) {
      if (startPage === 1) {
        endPage = Math.min(5, totalPages);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, totalPages - 4);
      }
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    const showFirstEllipsis = startPage > 1;
    const showLastEllipsis = endPage < totalPages;
    const isSinglePage = totalPages <= 1;

    return (
      <div className="mt-4" style={{ background: '#ffffff', padding: '12px 20px', borderRadius: '10px', border: '1px solid #e8ecf1' }}>
        <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: '15px' }}>
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '14px', color: '#6c7a8a', fontWeight: '500' }}>Show:</span>
            <select
              className="ml-2"
              style={{
                padding: '5px 28px 5px 10px',
                borderRadius: '6px',
                border: '1px solid #dce1e8',
                backgroundColor: 'white',
                fontSize: '14px',
                color: '#2c3e50',
                cursor: 'pointer',
                outline: 'none'
              }}
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
            <button
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                border: '1px solid #dce1e8',
                backgroundColor: 'white',
                color: (currentPage === 1 || isSinglePage) ? '#b8c5d0' : '#4a6cf7',
                fontSize: '18px',
                fontWeight: '500',
                cursor: (currentPage === 1 || isSinglePage) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: (currentPage === 1 || isSinglePage) ? 0.6 : 1,
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              disabled={currentPage === 1 || isSinglePage}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>

            {!isSinglePage && showFirstEllipsis && (
              <>
                <button
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid transparent',
                    backgroundColor: 'transparent',
                    color: '#4a6cf7',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                  onClick={() => handlePageChange(1)}
                >
                  1
                </button>
                <span style={{ color: '#b8c5d0', padding: '0 2px' }}>…</span>
              </>
            )}

            {isSinglePage ? (
              <button
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #4a6cf7',
                  backgroundColor: '#4a6cf7',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  minWidth: '34px',
                  cursor: 'default'
                }}
              >
                1
              </button>
            ) : (
              pages.map(page => (
                <button
                  key={page}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: page === currentPage ? '1px solid #4a6cf7' : '1px solid transparent',
                    backgroundColor: page === currentPage ? '#4a6cf7' : 'transparent',
                    color: page === currentPage ? 'white' : '#4a6cf7',
                    fontSize: '14px',
                    fontWeight: page === currentPage ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '34px'
                  }}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))
            )}

            {!isSinglePage && showLastEllipsis && (
              <>
                <span style={{ color: '#b8c5d0', padding: '0 2px' }}>…</span>
                <button
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid transparent',
                    backgroundColor: 'transparent',
                    color: '#4a6cf7',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                border: '1px solid #dce1e8',
                backgroundColor: 'white',
                color: (currentPage === totalPages || isSinglePage) ? '#b8c5d0' : '#4a6cf7',
                fontSize: '18px',
                fontWeight: '500',
                cursor: (currentPage === totalPages || isSinglePage) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: (currentPage === totalPages || isSinglePage) ? 0.6 : 1,
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              disabled={currentPage === totalPages || isSinglePage}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </div>

          <div style={{
            fontSize: '14px',
            color: '#6c7a8a',
            fontWeight: '400',
            background: '#f5f7fa',
            padding: '4px 14px',
            borderRadius: '20px',
            border: '1px solid #e8ecf1'
          }}>
            Showing {startRecord} to {endRecord} of {totalRecords} records
          </div>
        </div>
      </div>
    );
  };

  // ========== MAIN RENDER ==========
  return (
    <>
      {/* ===== BIG PRELOADER REMOVED – no longer needed ===== */}

      <div
        className={`modal fade ${isErrorWindowActive ? "show mymodal" : ""}`}
        tabIndex="-1"
        role="dialog"
        aria-hidden={!isErrorWindowActive}
        style={{ display: isErrorWindowActive ? "block" : "none" }}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content cdb-confirm-box col-sm-12">
            <button
              type="button"
              className="close text-right"
              aria-label="Close"
              onClick={() => {
                setErrorWindowActive(false);
                setErrorWindowMessage("");
              }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <p>{errorWindowMessage}</p>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <div className="row">
          <DynamicForm
            apiToken={apiToken} reportListData={reportListData}
            reportConfig={reportConfig} setReportConfig={setReportConfig}
            extraReportConfigFields={extraReportConfigFields} setExtraReportConfigFields={setExtraReportConfigFields}
            currentReportId={currentReportId} setCurrentReportId={setCurrentReportId}
            currentReportName={currentReportName} setCurrentReportName={setCurrentReportName}
            formState={formState} setFormState={setFormState}
            isFetchingConfig={isFetchingConfig} setIsFetchingConfig={setIsFetchingConfig}
            isErrorWindowActive={isErrorWindowActive} setErrorWindowActive={setErrorWindowActive}
            errorWindowMessage={errorWindowMessage} setErrorWindowMessage={setErrorWindowMessage}
          />

          {currentReportId && (areVisibleFieldsComplete() && !checkFetchingState()) && (
            <div className="col-md-2">
              <span className="f18">&nbsp;</span>
              <button
                disabled={isFetchingConfig}
                className={
                  isFetchingConfig
                    ? "form-control btn-primary p-2 btn-height disablesendreqbtn"
                    : "form-control btn-primary p-2 btn-height"
                }
                onClick={handleSubmit}
              >
                Send Request
              </button>
            </div>
          )}
        </div>

        {currentReportId && (
          <div className="mt-4">
            {/* ===== MANUAL REFRESH BUTTON COMMENTED OUT – auto‑polling handles updates every 5s ===== */}
            {/*
            <div className="float-right">
              <button
                className="btn btn-primary mb-1"
                onClick={() => getReportData(currentPage, pageSize)}
              >
                <ReloadOutlined />
              </button>
            </div>
            */}
            <div className="table-responsive">
              <table className="table text-center" style={{ tableLayout: "auto", width: "100%", verticalAlign: "middle" }}>
                <thead className="table-head">
                  <tr>
                    <th style={{ whiteSpace: "nowrap" }}>Request No.</th>
                    <th>Parameters Selected</th>
                    <th style={{ whiteSpace: "nowrap" }}>Submitted At</th>
                    <th style={{ whiteSpace: "nowrap" }}>Completed At</th>
                    <th style={{ whiteSpace: "nowrap" }}>Download</th>
                  </tr>
                </thead>
                {reportData.length > 0 ? (
                  <tbody>
                    {reportData.map((report) => (
                      <tr key={report.Request_No}>
                        <td style={{ whiteSpace: "nowrap" }}>{report.Request_No}</td>
                        <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{getInputData(report.Parameter_Selected)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {moment(report.Submitted_At).format("DD-MMM-YY HH:mm")}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {report.completed_at && moment(report.completed_at).format("DD-MMM-YY HH:mm")}
                        </td>
                        <td style={{ whiteSpace: "nowrap", fontSize: '20px' }}>
                          {report.Download_Link ? (
                            getHours(report.completed_at) <= 24 ? (
                              <a
                                onClick={() => downloadReportUpdateDate(report.Request_No, report.Download_Link)}
                                style={{ cursor: 'pointer', color: '#1890ff' }}
                              >
                                <DownloadOutlined />
                              </a>
                            ) : (
                              <span style={{ fontSize: '14px', color: '#999' }}>Expired</span>
                            )
                          ) : report.processed_flag == 3 ? (
                            <span style={{ fontSize: '14px', color: '#ff4d4f' }}>Invalid</span>
                          ) : (
                            <LoadingOutlined spin style={{ color: '#1890ff' }} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tr>
                    <td colSpan="5">
                      <div className="text-center py-3">No Report History found</div>
                    </td>
                  </tr>
                )}
              </table>
            </div>
            {renderPagination()}
          </div>
        )}
      </div>
    </>
  );
};

export default Form;